import {makeAutoObservable} from "mobx";
import {toast} from 'react-toastify';
import environment from '../environments';
import {apiFetch, notifyError} from '../api';

const emptyUser = () => ({});

class UserStore
{

    userList = [];
    stickerList = [];
    user = emptyUser();
    /* ID of the record in user, so a different record never shows stale data */
    loadedId = null;

    /* Cognito only offers forward pagination: keep the token of every visited page */
    pageTokens = [""];
    nextPageToken = "";
    searchRequest = 0;

    start = 0;
    limit = 50;
    searchTerm = "";

    /* Endpoints */
    detailRoute = "/users";
    dataEndpoint = environment.API_HOST + "/users/list";
    searchEndpoint = environment.API_HOST + "/users/search";

    /* Table configuration */
    sortBy = "registered";
    sortOrder = "asc";
    tableColumns = [
        {name: "E-Mail", field: "email", sortable: true},
        {name: "Registriert am", field: "registered", type: "date", sortable: true},
        {name: "Letzte Änderung", field: "modified", type: "date", sortable: true},
        {name: "Status", field: "status", sortable: true},
        {name: "Aktiv", field: "enabled", type: "bool", sortable: true},
    ]

    constructor()
    {
        makeAutoObservable(this)
    }

    getUserList()
    {
        const pageToken = this.pageTokens[this.pageTokens.length - 1];
        apiFetch(this.dataEndpoint + "?token=" + encodeURIComponent(pageToken) + "&limit=" + this.limit + "&sortBy=" +
            encodeURIComponent(this.sortBy) + "&sortOrder=" + encodeURIComponent(this.sortOrder))
            .then((json) =>
            {
                this.userList = json.data.users;
                this.nextPageToken = json.data.paginationToken || "";
            })
            .catch((error) =>
            {
                notifyError(error);
                return error;
            })
    }

    get hasNextPage()
    {
        return !this.searchTerm.trim() && this.nextPageToken !== "";
    }

    resetPagination()
    {
        this.pageTokens = [""];
        this.nextPageToken = "";
        this.start = 0;
    }

    sortLocally(users)
    {
        const factor = this.sortOrder === "desc" ? -1 : 1;
        return users.slice().sort((a, b) =>
        {
            const left = a[this.sortBy], right = b[this.sortBy];
            if (left === right) return 0;
            if (left === undefined || left === null) return 1;
            if (right === undefined || right === null) return -1;
            return (left > right ? 1 : -1) * factor;
        });
    }

    searchUser()
    {
        /* Ignore responses of searches that were superseded by a newer one */
        const request = ++this.searchRequest;
        const term = this.searchTerm.trim();

        if (term.length > 0)
        {
            apiFetch(this.searchEndpoint + "?term=" + encodeURIComponent(term))
                .then((json) =>
                {
                    if (request === this.searchRequest)
                    {
                        this.userList = this.sortLocally(json.data.users);
                    }
                })
                .catch((error) =>
                {
                    notifyError(error);
                })

        } else
        {
            this.resetPagination();
            this.getUserList();
        }
    }

    getUser(id)
    {
        if (this.loadedId !== id)
        {
            this.user = emptyUser();
            this.stickerList = [];
            this.loadedId = id;
        }

        apiFetch(environment.API_HOST + "/users/" + id)
            .then((json) =>
            {
                /* Ignore responses for a record that is no longer open */
                if (id !== this.loadedId)
                {
                    return;
                }

                this.user = json.data.user;
                this.stickerList = json.data.stickerList;
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    saveUser(id)
    {
        var payload = {
            email: this.user.email,
        }

        payload = JSON.stringify(payload);

        apiFetch(environment.API_HOST + "/users/" + id, {
            method: 'PUT',
            headers: {
                "Content-Type": "application/json"
            },
            body: payload
        })
            .then((json) =>
            {
                toast.success("Benutzer erfolgreich gespeichert.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    disableUser(id)
    {
        apiFetch(environment.API_HOST + "/users/" + id + "/disable", {
            method: 'POST',
        })
            .then((json) =>
            {
                toast.success("Benutzer erfolgreich deaktiviert.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    enableUser(id)
    {
        apiFetch(environment.API_HOST + "/users/" + id + "/enable", {
            method: 'POST',
        })
            .then((json) =>
            {
                toast.success("Benutzer erfolgreich aktiviert.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    resetPassword(id)
    {
        apiFetch(environment.API_HOST + "/users/" + id + "/reset", {
            method: 'POST',
        })
            .then((json) =>
            {
                toast.success("Kennwort erfolgreich zurückgesetzt.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    assignSticker(id, stickersToAssign)
    {
        var payload = {
            stickers: stickersToAssign,
        }

        payload = JSON.stringify(payload);

        apiFetch(environment.API_HOST + "/users/" + id + "/assign", {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: payload
        })
            .then((json) =>
            {
                toast.success("Sticker erfolgreich zugeordnet.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    unassignSticker(id, stickersToUnassign)
    {
        var payload = {
            stickers: [],
        }
        payload.stickers.push(stickersToUnassign);

        payload = JSON.stringify(payload);

        apiFetch(environment.API_HOST + "/users/" + id + "/unassign", {
            method: 'DELETE',
            headers: {
                "Content-Type": "application/json"
            },
            body: payload
        })
            .then((json) =>
            {
                toast.success("Zuordnung erfolgreich entfernt.");
                this.getUser(id);
            })
            .catch((error) =>
            {
                notifyError(error);
            })
    }

    sort(column)
    {
        this.sortBy = column;

        if (this.sortOrder === 'asc')
        {
            this.sortOrder = 'desc';
        } else
        {
            this.sortOrder = 'asc';
        }

        if (this.searchTerm.trim())
        {
            this.userList = this.sortLocally(this.userList);
        } else
        {
            this.getUserList();
        }
    }

    nextPage()
    {
        if (this.hasNextPage)
        {
            this.pageTokens.push(this.nextPageToken);
            this.start = this.start + this.limit;
            this.getUserList();
        }
    }

    previousPage()
    {
        if (this.pageTokens.length > 1)
        {
            this.pageTokens.pop();
            this.start = this.start - this.limit;
            this.getUserList();
        }
    }
}

const userStore = new UserStore();
export default userStore;
